import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/** @type {RegExp} */
const COLOR_OPTION_PATTERN = /color|colour|couleur|colore/i;

/** @type {RegExp} */
const COLOR_ALT_PATTERN = /^(.+)-\d+$/;

/** @type {string} */
const HIDDEN_CLASS = 'media-gallery__media--hidden';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const section = this.#getSection();

    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    section?.addEventListener('change', this.#handleVariantSelected, { signal });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#scheduleColorFilter();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getSection() {
    return this.closest('.shopify-section, .product-information, featured-product-information, dialog');
  }

  /**
   * @param {VariantUpdateEvent} [event]
   */
  #scheduleColorFilter(event) {
    const apply = () => this.#applyColorFilter(event);

    queueMicrotask(apply);
    requestAnimationFrame(apply);
    setTimeout(apply, 100);
  }

  #handleVariantSelected = () => {
    this.#scheduleColorFilter();
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    this.#scheduleColorFilter(event);
  };

  /**
   * @param {VariantUpdateEvent} [event]
   */
  #applyColorFilter(event) {
    const mediaElements = this.#getMediaElements();
    if (!mediaElements.length) return;

    if (!this.#productHasColorTaggedMedia()) {
      this.#showAllMedia();
      return;
    }

    const colorSlug = this.#getSelectedColorSlug(event);
    if (!colorSlug) {
      this.#showAllMedia();
      return;
    }

    this.#filterMediaByColor(colorSlug);
  }

  /**
   * @returns {boolean}
   */
  #productHasColorTaggedMedia() {
    return this.#getMediaElements().some(
      (element) => this.#getColorPrefixFromAlt(this.#getMediaAlt(element)) !== null
    );
  }

  #showAllMedia() {
    const slideshow = this.slideshow;
    const slides = [...this.querySelectorAll('slideshow-slide')].filter(
      (element) => element instanceof HTMLElement
    );

    slides.forEach((slide, index) => {
      this.#setElementVisibility(slide, true);

      const { thumbnails, dots } = slideshow?.refs ?? {};
      const thumbnail = thumbnails?.[index];
      if (thumbnail instanceof HTMLElement) this.#setElementVisibility(thumbnail, true);

      const dot = dots?.[index];
      if (dot instanceof HTMLElement) {
        this.#setElementVisibility(dot.closest('li') ?? dot, true);
      }
    });

    this.querySelectorAll('.media-gallery__grid > li').forEach((item) => {
      if (item instanceof HTMLElement) this.#setElementVisibility(item, true);
    });

    const zoomDialog = this.refs.zoomDialogComponent;
    const zoomMedia = zoomDialog?.refs.media ?? [];
    const zoomThumbnails = zoomDialog?.refs.thumbnails?.children ?? [];

    zoomMedia.forEach((media, index) => {
      if (!(media instanceof HTMLElement)) return;
      this.#setElementVisibility(media, true);

      const thumbnail = zoomThumbnails[index];
      if (thumbnail instanceof HTMLElement) this.#setElementVisibility(thumbnail, true);
    });
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getMediaElements() {
    return [
      ...this.querySelectorAll('slideshow-slide'),
      ...this.querySelectorAll('.media-gallery__grid > li'),
      ...(this.refs.zoomDialogComponent?.querySelectorAll('.dialog-zoomed-gallery > li') ?? []),
    ].filter((element) => element instanceof HTMLElement);
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeColorSlug(value) {
    return value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_\s]+/g, '-')
      .replace(/-+/g, '-');
  }

  /**
   * @param {string} alt
   * @returns {string | null}
   */
  #getColorPrefixFromAlt(alt) {
    const match = alt.trim().toLowerCase().match(COLOR_ALT_PATTERN);
    if (!match?.[1]) return null;
    return this.#normalizeColorSlug(match[1]);
  }

  /**
   * @param {string} alt
   * @param {string} colorSlug
   * @returns {boolean}
   */
  #shouldShowMedia(alt, colorSlug) {
    const prefix = this.#getColorPrefixFromAlt(alt);
    if (!prefix) return false;
    return prefix === colorSlug;
  }

  /**
   * @param {Element} element
   * @returns {string}
   */
  #getMediaAlt(element) {
    if (element instanceof HTMLElement) {
      const directAlt = element.getAttribute('data-media-alt')?.trim();
      if (directAlt) return directAlt.toLowerCase();
    }

    const mediaEl = element.querySelector('[data-media-alt], [data-media-id]');
    const dataAlt = mediaEl?.getAttribute('data-media-alt')?.trim();
    if (dataAlt) return dataAlt.toLowerCase();

    const img = element.querySelector('img.product-media__image, img');
    if (img instanceof HTMLImageElement && img.alt.trim()) {
      return img.alt.trim().toLowerCase();
    }

    if (img instanceof HTMLImageElement) {
      const src = img.currentSrc || img.src;
      if (src) {
        const filename = src.split('/').pop()?.split('?')[0] ?? '';
        const nameWithoutExt = filename.replace(/\.[^.]+$/, '');
        return decodeURIComponent(nameWithoutExt).toLowerCase();
      }
    }

    return '';
  }

  /**
   * @param {HTMLElement} element
   * @param {boolean} visible
   */
  #setElementVisibility(element, visible) {
    element.classList.toggle(HIDDEN_CLASS, !visible);

    if (visible) {
      element.removeAttribute('hidden');
    } else {
      element.setAttribute('hidden', '');
    }
  }

  /**
   * @param {HTMLElement} slide
   * @returns {string | null}
   */
  #getSlideId(slide) {
    const existingId = slide.getAttribute('slide-id');
    if (existingId) return existingId;

    const mediaId = slide.querySelector('[data-media-id]')?.getAttribute('data-media-id');
    if (!mediaId) return null;

    slide.setAttribute('slide-id', mediaId);
    return mediaId;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    return this.#getSection()?.querySelector('variant-picker') ?? null;
  }

  /**
   * @param {VariantUpdateEvent} [event]
   * @returns {string[]}
   */
  #getSelectedOptionValues(event) {
    /** @type {string[]} */
    const values = [];
    const variantPicker = this.#getVariantPicker();

    if (variantPicker) {
      for (const input of variantPicker.querySelectorAll('fieldset input:checked')) {
        if (input instanceof HTMLInputElement && input.value) {
          values.push(input.value);
        }
      }

      for (const select of variantPicker.querySelectorAll('select')) {
        const option = select.options[select.selectedIndex];
        if (option?.value) values.push(option.value);
      }

      const variantJson = variantPicker.querySelector('script[type="application/json"]')?.textContent;
      if (variantJson) {
        try {
          const variant = /** @type {Record<string, unknown>} */ (JSON.parse(variantJson));
          for (const option of [variant.option1, variant.option2, variant.option3]) {
            if (typeof option === 'string' && option && !values.includes(option)) {
              values.push(option);
            }
          }
        } catch {
          // Ignore invalid JSON while the picker is morphing.
        }
      }
    }

    const variant = /** @type {Record<string, unknown> | null | undefined} */ (event?.detail?.resource);
    for (const option of [variant?.option1, variant?.option2, variant?.option3]) {
      if (typeof option === 'string' && option && !values.includes(option)) {
        values.push(option);
      }
    }

    return values;
  }

  /**
   * @param {HTMLElement} variantPicker
   * @returns {string | null}
   */
  #getColorFromVariantPicker(variantPicker) {
    for (const fieldset of variantPicker.querySelectorAll('fieldset')) {
      const legend = fieldset.querySelector('legend')?.textContent ?? '';
      if (!COLOR_OPTION_PATTERN.test(legend)) continue;

      const checked = fieldset.querySelector('input:checked');
      if (checked instanceof HTMLInputElement && checked.value) {
        return this.#normalizeColorSlug(checked.value);
      }
    }

    for (const select of variantPicker.querySelectorAll('select')) {
      const label = select.closest('.variant-option')?.querySelector('label')?.textContent ?? '';
      if (!COLOR_OPTION_PATTERN.test(label)) continue;

      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption?.value) {
        return this.#normalizeColorSlug(selectedOption.value);
      }
    }

    return null;
  }

  /**
   * @param {VariantUpdateEvent} [event]
   * @returns {string | null}
   */
  #getSelectedColorSlug(event) {
    const prefixes = new Set(
      this.#getMediaElements()
        .map((element) => this.#getColorPrefixFromAlt(this.#getMediaAlt(element)))
        .filter((prefix) => prefix !== null)
    );

    if (prefixes.size === 0) return null;

    const selectedValues = this.#getSelectedOptionValues(event);

    for (const value of selectedValues) {
      const slug = this.#normalizeColorSlug(value);
      if (prefixes.has(slug)) return slug;
    }

    const variantPicker = this.#getVariantPicker();
    if (variantPicker) {
      const colorFromPicker = this.#getColorFromVariantPicker(variantPicker);
      if (colorFromPicker && prefixes.has(colorFromPicker)) return colorFromPicker;
    }

    return null;
  }

  /**
   * @param {string} colorSlug
   */
  #filterMediaByColor(colorSlug) {
    const slideshow = this.slideshow;
    const slides = [...this.querySelectorAll('slideshow-slide')].filter(
      (element) => element instanceof HTMLElement
    );
    /** @type {string | null} */
    let firstVisibleSlideId = null;

    slides.forEach((slide, index) => {
      const visible = this.#shouldShowMedia(this.#getMediaAlt(slide), colorSlug);
      this.#setElementVisibility(slide, visible);

      const slideId = this.#getSlideId(slide);
      if (visible && !firstVisibleSlideId && slideId) {
        firstVisibleSlideId = slideId;
      }

      const { thumbnails, dots } = slideshow?.refs ?? {};
      const thumbnail = thumbnails?.[index];
      if (thumbnail instanceof HTMLElement) this.#setElementVisibility(thumbnail, visible);

      const dot = dots?.[index];
      if (dot instanceof HTMLElement) {
        this.#setElementVisibility(dot.closest('li') ?? dot, visible);
      }
    });

    this.querySelectorAll('.media-gallery__grid > li').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      this.#setElementVisibility(item, this.#shouldShowMedia(this.#getMediaAlt(item), colorSlug));
    });

    const zoomDialog = this.refs.zoomDialogComponent;
    const zoomMedia = zoomDialog?.refs.media ?? [];
    const zoomThumbnails = zoomDialog?.refs.thumbnails?.children ?? [];

    zoomMedia.forEach((media, index) => {
      if (!(media instanceof HTMLElement)) return;

      const visible = this.#shouldShowMedia(this.#getMediaAlt(media), colorSlug);
      this.#setElementVisibility(media, visible);

      const thumbnail = zoomThumbnails[index];
      if (thumbnail instanceof HTMLElement) {
        this.#setElementVisibility(thumbnail, visible);
      }
    });

    if (slideshow && firstVisibleSlideId) {
      slideshow.select({ id: firstVisibleSlideId }, undefined, { animate: false });
    }
  }

  /**
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    const slide = [...this.querySelectorAll('slideshow-slide')][event.detail.index];
    const slideId = slide instanceof HTMLElement ? this.#getSlideId(slide) : null;

    if (slideId) {
      this.slideshow?.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
